import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Info, AlertCircle } from 'lucide-react';
import { getLymphocyteTrend } from '../utils/api';
import { formatDateByCountry, formatDisplayDateWithMonth } from '../utils/dateUtils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

const LymphocyteTrendPage = () => {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(24); // 12, 24, or 0 for all

  useEffect(() => {
    const fetchTrend = async () => {
      setLoading(true);
      try {
        const data = await getLymphocyteTrend(selectedPeriod);
        setTrendData(data);
      } catch (err) {
        console.error('Failed to fetch lymphocyte trend:', err);
        setError('Unable to load trend data');
      } finally {
        setLoading(false);
      }
    };
    fetchTrend();
  }, [selectedPeriod]);

  // Calculate trend line points for the chart
  const getChartData = () => {
    if (!trendData?.readings) return [];
    
    const readings = trendData.readings.filter(r => !r.excluded);
    if (readings.length === 0) return [];

    // Parse dates and create chart data
    const chartData = readings.map(r => ({
      date: r.date,
      dateLabel: formatDateByCountry(r.date, 'AU', { includeDay: true, shortMonth: true, shortYear: true }),
      lymphocytes: r.lymphocytes,
      excluded: r.excluded
    }));

    // Add trend line if we have slope data
    if (trendData.slope && trendData.trend_status === 'doubling') {
      const firstDate = new Date(readings[0].date);
      const lastDate = new Date(readings[readings.length - 1].date);
      
      // Calculate trend line values using: y = y0 * e^(slope * days)
      const y0 = readings[0].lymphocytes;
      
      chartData.forEach((point, index) => {
        const days = (new Date(point.date) - firstDate) / (1000 * 60 * 60 * 24);
        point.trendLine = y0 * Math.exp(trendData.slope * days);
      });
    }

    return chartData;
  };

  const chartData = getChartData();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Dashboard
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const getTrendIcon = () => {
    if (trendData.trend_status === 'stable_or_decreasing') {
      return trendData.slope < -0.0001 
        ? <TrendingDown className="h-8 w-8 text-green-600" />
        : <Minus className="h-8 w-8 text-green-600" />;
    }
    return <TrendingUp className="h-8 w-8 text-blue-600" />;
  };

  const getTrendColor = () => {
    return trendData.trend_status === 'stable_or_decreasing' ? 'green' : 'blue';
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/dashboard"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className={`p-4 bg-${getTrendColor()}-50 rounded-2xl`}>
          {getTrendIcon()}
        </div>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Lymphocyte Trend Rate</h1>
          {trendData.has_sufficient_data ? (
            trendData.trend_status === 'doubling' ? (
              <p className="text-2xl font-semibold text-slate-700">
                Doubling time {trendData.doubling_time_friendly}
              </p>
            ) : (
              <p className="text-2xl font-semibold text-green-600">
                {trendData.slope < -0.0001 ? 'Decreasing' : 'Stable'}
              </p>
            )
          ) : (
            <p className="text-lg text-slate-600">Insufficient data</p>
          )}
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex gap-2">
        {[
          { value: 12, label: '12 months' },
          { value: 24, label: '24 months' },
          { value: 0, label: 'All time' }
        ].map(period => (
          <button
            key={period.value}
            onClick={() => setSelectedPeriod(period.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === period.value
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {period.label}
          </button>
        ))}
      </div>

      {/* Disclaimer Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            {trendData.disclaimer}
          </p>
        </div>
      </div>

      {/* Insufficient Data Message */}
      {!trendData.has_sufficient_data && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
          <Info className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Not Enough Data Yet</h3>
          <p className="text-slate-600 mb-4">
            Add at least 3 lymphocyte results across 6+ months to see your trend rate.
          </p>
          <p className="text-sm text-slate-500">
            You currently have {trendData.readings_used} reading{trendData.readings_used !== 1 ? 's' : ''}
            {trendData.months_spanned > 0 && ` spanning ${Math.round(trendData.months_spanned)} months`}
          </p>
          <Link
            to="/blood-results"
            className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Blood Results
          </Link>
        </div>
      )}

      {/* Chart */}
      {trendData.has_sufficient_data && chartData.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Lymphocyte Count Over Time</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="dateLabel" 
                  tick={{ fontSize: 12 }}
                  stroke="#64748b"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#64748b"
                  label={{ value: '×10⁹/L', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                  formatter={(value, name) => [
                    `${value.toFixed(2)} ×10⁹/L`,
                    name === 'lymphocytes' ? 'Lymphocytes' : 'Trend Line'
                  ]}
                />
                <Legend />
                
                {/* Actual readings */}
                <Line
                  type="monotone"
                  dataKey="lymphocytes"
                  name="Lymphocytes"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7 }}
                />
                
                {/* Trend line (dashed) */}
                {trendData.trend_status === 'doubling' && (
                  <Line
                    type="monotone"
                    dataKey="trendLine"
                    name="Trend Line"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* R-squared indicator */}
          {trendData.r_squared !== null && (
            <p className="text-xs text-slate-500 mt-4 text-center">
              Trend fit quality (R²): {(trendData.r_squared * 100).toFixed(1)}%
              {trendData.r_squared > 0.8 && ' — Good fit'}
              {trendData.r_squared <= 0.8 && trendData.r_squared > 0.5 && ' — Moderate fit'}
              {trendData.r_squared <= 0.5 && ' — Poor fit, results may be less reliable'}
            </p>
          )}
        </div>
      )}

      {/* Readings List */}
      {trendData.readings && trendData.readings.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Readings Used</h2>
          <div className="space-y-2">
            {trendData.readings.map((reading, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  reading.excluded 
                    ? 'bg-amber-50 border border-amber-200' 
                    : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-900">
                    {formatDisplayDateWithMonth(reading.date, 'AU')}
                  </span>
                  {reading.excluded && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                      Excluded (illness)
                    </span>
                  )}
                </div>
                <span className={`font-semibold ${reading.excluded ? 'text-amber-600' : 'text-slate-900'}`}>
                  {reading.lymphocytes.toFixed(2)} ×10⁹/L
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">
            {trendData.readings.filter(r => r.excluded).length > 0 && (
              <>Readings marked during illness are excluded from the trend calculation.</>
            )}
          </p>
        </div>
      )}

      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Info className="h-5 w-5" />
          Understanding Lymphocyte Doubling Time
        </h3>
        <div className="space-y-3 text-sm text-blue-800">
          <p>
            <strong>What is it?</strong> Lymphocyte doubling time is a measure of how quickly your lymphocyte count is increasing. 
            It's calculated by fitting a mathematical curve to your readings over time.
          </p>
          <p>
            <strong>How is it used?</strong> Lymphocyte doubling time is one of several factors that doctors may consider when 
            monitoring CLL. A faster doubling time (shorter period) may indicate more active disease, while a slower doubling 
            time (longer period) may suggest more stable disease.
          </p>
          <p>
            <strong>Important:</strong> This calculation is based solely on your entered results. It does not account for 
            other clinical factors your doctor considers. A single metric should never be used in isolation to make treatment decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LymphocyteTrendPage;
